import { z } from 'zod';
import { DocumentType } from './enums.js';
import { RoutingConfig, SEED_ROUTING_CONFIG } from './routing.js';

// ── Template catalogs (stored as JSON columns on TemplateConfig) ─────────────

export const Job = z.object({ jobId: z.string().min(1), title: z.string().min(1) });
export type Job = z.infer<typeof Job>;

export const VenueRole = z.object({
  roleId: z.string().min(1),
  name: z.string().min(1),
  sodConflicts: z.array(z.string()).default([]),
});
export type VenueRole = z.infer<typeof VenueRole>;

export const WorkflowDefinition = z.object({
  key: z.string().min(1),
  name: z.string().min(1),
  documentType: DocumentType,
  xmlBlobUrl: z.string().url(),
  version: z.number().int().positive(),
});
export type WorkflowDefinition = z.infer<typeof WorkflowDefinition>;

export const JobApprovalLimit = z.object({
  jobId: z.string().min(1),
  documentType: DocumentType,
  amountUsd: z.number().nonnegative(),
});
export type JobApprovalLimit = z.infer<typeof JobApprovalLimit>;

export const TemplateConfig = z.object({
  id: z.string().min(1),
  templateLegalEntityId: z.string().min(1).max(4),
  copyProjectName: z.string().min(1),
  peopleImportProjectName: z.string().min(1),
  lastDryRunAt: z.string().datetime().nullable(),
  lastTemplateChangeAt: z.string().datetime().nullable(),
  workflowLibrary: z.array(WorkflowDefinition),
  roleCatalog: z.array(VenueRole),
  routing: RoutingConfig,
  jobCatalog: z.array(Job),
  idRuleRegex: z.string().min(1),
  active: z.boolean(),
});
export type TemplateConfig = z.infer<typeof TemplateConfig>;

// ── Seed values (spec section 6 seed + 21 defaults). Synthetic demo data. ────

export const SEED_JOB_CATALOG: Job[] = [
  { jobId: 'VENUE-GM', title: 'Venue general manager' },
  { jobId: 'FIN-MGR', title: 'Venue finance manager' },
  { jobId: 'BUYER', title: 'Buyer' },
  { jobId: 'AP-CLERK', title: 'Accounts payable clerk' },
  { jobId: 'PROJ-MGR', title: 'Project manager' },
  { jobId: 'REG-FIN-DIR', title: 'Regional finance director' },
  // The GL accountant's position reports to the finance director (position 000613, job
  // Finance-director in the sales-cd9 demo environment; VERIFY per environment).
  { jobId: 'GL-ACCOUNTANT', title: 'GL accountant' },
  { jobId: 'FINANCE-DIRECTOR', title: 'Finance director' },
];

/**
 * Security roles a venue user can get: D365's own role identifiers (SecurityRoleIdentifier,
 * confirmed on the DEV box 2026-10-14), so the people file names the role D365 assigns. Each is
 * scoped to the venue company when assigned. Also the allow list for live assignment
 * (`ASSIGNABLE_D365_ROLES` in d365-client mirrors it): never an admin role.
 */
export const SEED_ROLE_CATALOG: VenueRole[] = [
  { roleId: 'LEDGERACCOUNTINGMANAGER', name: 'Accounting manager', sodConflicts: [] },
  { roleId: 'LEDGERACCOUNTANT', name: 'Accountant', sodConflicts: [] },
  {
    roleId: 'VENDPURCHASINGAGENT',
    name: 'Purchasing agent',
    sodConflicts: ['VENDINVOICEACCOUNTSPAYABLEMANAGER'],
  },
  { roleId: 'VENDINVOICEACCOUNTSPAYABLECLERK', name: 'Accounts payable clerk', sodConflicts: [] },
  {
    roleId: 'VENDINVOICEACCOUNTSPAYABLEMANAGER',
    name: 'Accounts payable manager',
    sodConflicts: ['VENDPURCHASINGAGENT'],
  },
  { roleId: 'PROJPROJECTMANAGER', name: 'Project manager', sodConflicts: [] },
  { roleId: 'LEDGERFINANCIALCONTROLLER', name: 'Financial controller', sodConflicts: [] },
];

const limits = (jobId: string, pr: number, po: number, vi: number, jnl: number): JobApprovalLimit[] => [
  { jobId, documentType: 'PurchaseRequisition', amountUsd: pr },
  { jobId, documentType: 'PurchaseOrder', amountUsd: po },
  { jobId, documentType: 'VendorInvoice', amountUsd: vi },
  { jobId, documentType: 'Journal', amountUsd: jnl },
];

/** Template default approval limits per job; 0 means "cannot approve". */
export const SEED_JOB_LIMITS: JobApprovalLimit[] = [
  ...limits('BUYER', 5_000, 5_000, 0, 0),
  ...limits('AP-CLERK', 0, 0, 2_500, 0),
  ...limits('PROJ-MGR', 10_000, 10_000, 10_000, 0),
  ...limits('FIN-MGR', 25_000, 25_000, 25_000, 25_000),
  ...limits('VENUE-GM', 100_000, 100_000, 100_000, 50_000),
  ...limits('REG-FIN-DIR', 500_000, 500_000, 500_000, 500_000),
  ...limits('GL-ACCOUNTANT', 0, 0, 0, 0),
  ...limits('FINANCE-DIRECTOR', 500_000, 500_000, 500_000, 500_000),
];

// Workflow XML location is environment config, not a D365 assumption.
const WORKFLOW_XML_BASE = 'https://lgvenuestorage.blob.core.windows.net/workflows';

export const SEED_WORKFLOW_LIBRARY: WorkflowDefinition[] = [
  {
    key: 'PR',
    name: 'Purchase requisition review',
    documentType: 'PurchaseRequisition',
    xmlBlobUrl: `${WORKFLOW_XML_BASE}/PR-v1.xml`,
    version: 1,
  },
  {
    key: 'PO',
    name: 'Purchase order approval',
    documentType: 'PurchaseOrder',
    xmlBlobUrl: `${WORKFLOW_XML_BASE}/PO-v1.xml`,
    version: 1,
  },
  {
    key: 'VI',
    name: 'Vendor invoice approval',
    documentType: 'VendorInvoice',
    xmlBlobUrl: `${WORKFLOW_XML_BASE}/VI-v1.xml`,
    version: 1,
  },
  {
    key: 'JNL',
    name: 'Journal approval',
    documentType: 'Journal',
    xmlBlobUrl: `${WORKFLOW_XML_BASE}/JNL-v1.xml`,
    version: 1,
  },
];

/**
 * Template answer defaults (spec 8.1 "default from template"). Kept as a
 * constant keyed by template until TemplateConfig gains a defaults column
 * (data-model change, needs approval).
 */
export interface TemplateDefaults {
  currency: string;
  fiscalCalendar: string;
  chartOfAccounts: string;
  matchingPolicy: 'TwoWay' | 'ThreeWay';
  copyNumberSequences: boolean;
  resetToSmallest: boolean;
}

export const TEMPLATE_DEFAULTS: Record<string, TemplateDefaults> = {
  LTPL: {
    currency: 'USD',
    fiscalCalendar: 'LG-FISCAL',
    chartOfAccounts: 'LG-COA',
    matchingPolicy: 'ThreeWay',
    copyNumberSequences: true,
    resetToSmallest: true,
  },
  // Convention centers buy services against event contracts: two-way matching by default.
  LTPC: {
    currency: 'USD',
    fiscalCalendar: 'LG-FISCAL',
    chartOfAccounts: 'LG-COA',
    matchingPolicy: 'TwoWay',
    copyNumberSequences: true,
    resetToSmallest: true,
  },
};

/** What each template is for, in words (shown when choosing one). */
export const TEMPLATE_LABELS: Record<string, string> = {
  LTPL: 'Legends template company: stadiums and arenas (procurement, AP, GL, projects; three-way matching)',
  LTPC: 'Legends convention-center template (procurement, AP, GL, projects; two-way matching)',
};

export function templateDefaults(templateId: string): TemplateDefaults {
  const d = TEMPLATE_DEFAULTS[templateId] ?? TEMPLATE_DEFAULTS['LTPL'];
  if (!d) throw new Error('No template defaults configured');
  return d;
}

/**
 * Seed template. Dates are relative to `asOf` so a reused demo never shows a
 * dry run older than the last template change.
 */
export function seedTemplateConfig(asOf: Date): TemplateConfig {
  const day = 24 * 60 * 60 * 1000;
  return {
    id: 'LTPL',
    templateLegalEntityId: 'LTPL',
    copyProjectName: 'LG-VENUE-COPY',
    peopleImportProjectName: 'LG-VENUE-PEOPLE',
    lastTemplateChangeAt: new Date(asOf.getTime() - 14 * day).toISOString(),
    lastDryRunAt: new Date(asOf.getTime() - 7 * day).toISOString(),
    workflowLibrary: SEED_WORKFLOW_LIBRARY,
    roleCatalog: SEED_ROLE_CATALOG,
    routing: SEED_ROUTING_CONFIG,
    jobCatalog: SEED_JOB_CATALOG,
    idRuleRegex: '^V[0-9]{3}$',
    active: true,
  };
}

/**
 * Every seeded template: LTPL, and LTPC for convention centers. LTPC copies from its own
 * template company with its own copy project; jobs, roles, workflows and routing are shared.
 */
export function seedTemplateConfigs(asOf: Date): TemplateConfig[] {
  const day = 24 * 60 * 60 * 1000;
  const ltpl = seedTemplateConfig(asOf);
  return [
    ltpl,
    {
      ...ltpl,
      id: 'LTPC',
      templateLegalEntityId: 'LTPC',
      copyProjectName: 'LG-CC-COPY',
      lastTemplateChangeAt: new Date(asOf.getTime() - 21 * day).toISOString(),
      lastDryRunAt: new Date(asOf.getTime() - 9 * day).toISOString(),
    },
  ];
}
